import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";

const GLOBE_URL = "/glob.png";

const FACTS = [
    {
        number: "5 Seconds",
        intro: "To make someone",
        emphasis: "stop scrolling.",
    },
    {
        number: "50 Milliseconds",
        intro: "To form a",
        emphasis: "First impression",
        emphasisTail: " of your website.",
    },
    {
        number: "1 Scroll",
        intro: "Can decide if your story gets",
        emphasis: "read or skipped.",
    },
    {
        number: "300%",
        intro: "Average ROI increase with",
        emphasis: "performance marketing.",
    },
    {
        number: "1 Commmmuniti",
        intro: "Is what it takes to turn",
        emphasis: "engagement into conversions.",
    },
];

const STEP_COUNT = FACTS.length;

export default function FunFacts() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end end"],
    });

    const globeRotate = useTransform(scrollYProgress, [0, 1], [0, 720]);
    const globeScale = useTransform(scrollYProgress, [0, 0.5, 1], [1, 1.08, 1]);

    const activeIndex = useTransform(scrollYProgress, (v) => {
        return Math.floor(v * STEP_COUNT);
    });

    return (
        <section
            ref={ref}
            id="facts"
            data-testid="fun-facts-section"
            className="relative text-white"
            style={{ backgroundColor: "#0d3a6e", height: `${Math.max(STEP_COUNT * 120, 200)}vh` }}
        >
            <div
                className="sticky top-0 h-screen w-full flex flex-col"
                style={{ position: "sticky", top: 0, zIndex: 10 }}
            >
                <motion.div
                    style={{
                        rotate: globeRotate,
                        scale: globeScale,
                        backgroundImage: `url(${GLOBE_URL})`,
                        backgroundSize: "contain",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                    }}
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[50vh] w-[50vh] sm:h-[60vh] sm:w-[60vh] lg:h-[70vh] lg:w-[70vh] opacity-[0.22] pointer-events-none mix-blend-screen"
                >
                </motion.div>

                {/* Top eyebrow + headline */}
                <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-10 pt-28 lg:pt-32 w-full">
                    <p className="label-eyebrow-light">Fun Facts</p>
                    <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-3xl leading-[0.95]">
                        <span className="block">
                            The internet{" "}
                            <span className="font-serif-it font-normal text-white/55">
                                isn't
                            </span>{" "}
                            crowded.
                        </span>
                        <span className="block font-bold text-white">
                            It's selective.
                        </span>
                    </h2>
                </div>

                {/* Step indicator */}
                <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-10 mt-10 w-full">
                    <FactProgress activeIndex={activeIndex} />
                </div>

                {/* Active fact (centered) */}
                <div className="relative z-10 flex-1 flex items-center justify-center w-full px-6 lg:px-10">
                    <ActiveFact activeIndex={activeIndex} />
                </div>

                {/* Scroll hint at the bottom */}
                <ScrollHint activeIndex={activeIndex} />
            </div>
        </section>
    );
}

function FactProgress({ activeIndex }) {
    return (
        <div className="flex items-center gap-3">
            {FACTS.map((_, i) => (
                <StepBar key={i} activeIndex={activeIndex} index={i} />
            ))}
        </div>
    );
}

function StepBar({ activeIndex, index }) {
    const width = useTransform(activeIndex, (v) => {
        if (v > index) return "100%";
        if (Math.floor(v) === index) return "60%";
        return "0%";
    });
    return (
        <span className="h-px bg-white/25 flex-1 origin-left relative overflow-hidden rounded-full">
            <motion.span
                className="absolute inset-y-0 left-0 bg-white"
                style={{ width }}
            />
        </span>
    );
}

function ActiveFact({ activeIndex }) {
    const [idx, setIdx] = useTrackMotion(activeIndex);
    const fact = FACTS[Math.min(idx, FACTS.length - 1)] || FACTS[0];

    return (
        <div className="w-full max-w-5xl flex-1 flex flex-col items-center justify-center">
            <div className="flex items-center gap-4 text-white/55 mb-5">
                <span className="font-display text-lg">0{idx + 1}</span>
                <span className="h-px w-12 bg-white/40" />
                <span className="label-eyebrow-light">Fact 0{idx + 1} / 0{STEP_COUNT}</span>
            </div>

            <AnimatePresence mode="wait">
                <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -40 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="will-change-transform text-center"
                >
                    <p className="font-display font-bold text-white text-[10vw] sm:text-[8vw] lg:text-[6.4rem] leading-[0.95] tracking-[-0.03em]">
                        {fact.number}
                    </p>

                    <div className="mt-3 max-w-2xl mx-auto">
                        <p className="text-lg sm:text-xl lg:text-2xl text-white/85 leading-snug">
                            {fact.intro}
                        </p>
                        <p className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                            {fact.emphasis}
                            {fact.emphasisTail && (
                                <span className="font-body text-lg sm:text-xl lg:text-2xl font-normal text-white/85">
                                    {fact.emphasisTail}
                                </span>
                            )}
                        </p>
                    </div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
}

// Subscribe to a motion value as React state
function useTrackMotion(motionValue) {
    const [val, setVal] = useState(Math.floor(motionValue.get()));
    useEffect(() => {
        const unsubscribe = motionValue.on("change", (v) => {
            setVal(Math.floor(v));
        });
        return () => {
            if (typeof unsubscribe === "function") {
                unsubscribe();
            }
        };
    }, [motionValue]);
    return [val, setVal];
}

function ScrollHint({ activeIndex }) {
    const [idx] = useTrackMotion(activeIndex);
    if (idx >= STEP_COUNT - 1) return null;
    return (
        <div className="relative z-10 pb-10 flex flex-col items-center gap-2 text-white/55">
            <span className="text-[10px] uppercase tracking-[0.3em]">Scroll to continue</span>
            <motion.span
                initial={{ y: 0 }}
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                className="block h-6 w-px bg-white/40"
            />
        </div>
    );
}
